import {
    PLAN_LIMIT_BILLING_PATH,
    type PlanAllowance,
    type PlanLimitNotice,
    planAllowanceNotice,
    planLimitHolds,
    topPlanAllowance,
} from '@zerohuman/shared'
import { type ComputedRef, computed, type Ref, ref } from 'vue'
import { api, type Me } from '@/api'
import { useUiMode } from '@/composables/useUiMode'
import { grantAllowsScreen } from '@/lib/navGrant'

/** Where a dismissed near banner is remembered in the browser: per enterprise, unit and period (CTO decision 8). */
const DISMISSED_PREFIX = 'zh.planLimit.dismissed'

/** The banner the shell shows: the allowance it is about, its words and button, and how it looks. */
export type PlanLimitBanner = {
    allowance: PlanAllowance
    notice: PlanLimitNotice
    /**
     * `blocked` only where the limit holds something back (paused work, or a mailbox's held mail); near a limit, or
     * runs waiting for a slot, is `warning`.
     */
    tone: 'warning' | 'blocked'
    /** Only a limit that holds something back stays until it clears; a near one, or full runs at once, can be put away. */
    dismissible: boolean
    /** Whether the person's access includes Billing, where the button goes. */
    showsButton: boolean
}

/** The key one near banner's dismissal is kept under: a new period, or another unit, is a different key. */
export function planLimitDismissalKey(enterpriseId: string, allowance: PlanAllowance): string {
    return `${DISMISSED_PREFIX}:${enterpriseId}:${allowance.unit}:${allowance.resetsAt ?? 'held'}`
}

/** The browser's storage, or null where it refuses access (a private window): nothing is then remembered. */
function browserStorage(): Storage | null {
    try {
        const storage = globalThis.localStorage
        return storage && typeof storage.getItem === 'function' ? storage : null
    } catch {
        return null
    }
}

function dismissedIn(key: string): boolean {
    try {
        return browserStorage()?.getItem(key) === '1'
    } catch {
        return false
    }
}

function rememberDismissed(key: string): void {
    try {
        browserStorage()?.setItem(key, '1')
    } catch {
        // A browser that refuses storage still hides the banner for this page (the composable keeps the dismissal),
        // and shows it again on the next load; nothing else is lost.
    }
}

/**
 * The plan-limit banner across the portal (#2122): the signed-in enterprise's allowances, read when the shell mounts
 * and again after each route change, and the one banner they make. A limit that holds something back (paused work,
 * or a mailbox's new mail held at its daily limit, #805) beats one coming close, which beats runs at once, and the
 * highest share of its limit wins, so the banner never stacks. A banner the person dismissed stays away for that unit
 * and period, across reloads; a unit is back once it holds something back, or in the next period (the next UTC day,
 * for a mailbox's mail). Full runs at once pause nothing (new runs wait for a slot), so that one is a calm, closable
 * warning, never a blocking banner. The words are the shared sentence in the person's Standard or Advanced mode; the
 * button shows only when their access includes Billing, and without it the words say who to ask to upgrade.
 */
export function usePlanLimitBanner(me: Ref<Me | null>): {
    banner: ComputedRef<PlanLimitBanner | null>
    refresh: () => Promise<void>
    dismiss: () => void
} {
    const allowances = ref<PlanAllowance[]>([])
    // Bumped on each dismissal, so the banner reads the browser's storage again.
    const dismissals = ref(0)
    // Dismissals made on this page, so the close button works even where the browser refuses storage.
    const dismissedHere = new Set<string>()
    const { mode } = useUiMode()

    const banner = computed<PlanLimitBanner | null>(() => {
        void dismissals.value
        const person = me.value
        if (!person) {
            return null
        }
        const enterpriseId = person.enterprise.id
        // Only a limit that holds something back stays until it clears; anything else the person may put away.
        const allowance = topPlanAllowance(allowances.value, item => {
            if (planLimitHolds(item.unit, item.state)) {
                return false
            }
            const key = planLimitDismissalKey(enterpriseId, item)
            return dismissedHere.has(key) || dismissedIn(key)
        })
        const showsButton = grantAllowsScreen(
            { isOwner: person.isOwner, scopes: person.scopes },
            PLAN_LIMIT_BILLING_PATH,
        )
        // Without Billing the words say who to ask to upgrade, rather than telling the person to do it.
        const notice = allowance ? planAllowanceNotice(allowance, mode.value, showsButton) : null
        if (!allowance || !notice) {
            return null
        }
        const holds = planLimitHolds(allowance.unit, allowance.state)
        return {
            allowance,
            notice,
            tone: holds ? 'blocked' : 'warning',
            dismissible: !holds,
            showsButton,
        }
    })

    async function refresh(): Promise<void> {
        if (!me.value) {
            return
        }
        try {
            allowances.value = await api.planAllowances()
        } catch {
            // A read that fails keeps what the last one said: the banner never guesses a limit cleared.
        }
    }

    function dismiss(): void {
        const shown = banner.value
        if (!shown?.dismissible || !me.value) {
            return
        }
        const key = planLimitDismissalKey(me.value.enterprise.id, shown.allowance)
        dismissedHere.add(key)
        rememberDismissed(key)
        dismissals.value += 1
    }

    return { banner, refresh, dismiss }
}
